import { Account, type AccountConstructor } from '@webapp/platform/finance'
import { type ExpenseStatusEnum } from '../enums/ExpenseStatusEnum'
import { type ExpenseTypeEnum } from '../enums/ExpenseTypeEnum'

export interface ApprovedExpenseItemRequesterReference {
  id: number
  firstName: string
  lastName: string
}

export interface ApprovedExpenseItemReportReference {
  id: number
  uuid: string
  status: ExpenseStatusEnum
  createdDate: ISODateString
  hasSiblingItems: boolean
  notes: Nullable<string>
}

export interface ApprovedExpenseItemConstructor {
  id: number
  lineNumber: number
  merchant: string
  amount: string
  currencyId: number
  expenseType: ExpenseTypeEnum
  expenseTypeLabel: string
  attachment: Nullable<string>
  notes: Nullable<string>
  approvedDate: Nullable<ISODateString>
  requester: ApprovedExpenseItemRequesterReference
  expenseReport: ApprovedExpenseItemReportReference
  account: AccountConstructor
}

export class ApprovedExpenseItem {
  readonly id: number
  readonly lineNumber: number
  readonly merchant: string
  readonly amount: string
  readonly currencyId: number
  readonly expenseType: ExpenseTypeEnum
  readonly expenseTypeLabel: string
  readonly attachment: Nullable<string>
  readonly notes: Nullable<string>
  readonly approvedDate: Nullable<Date>
  readonly requester: ApprovedExpenseItemRequesterReference
  readonly expenseReport: ApprovedExpenseItemReportReference
  readonly account: Account

  constructor(data: ApprovedExpenseItemConstructor) {
    this.id = data.id
    this.lineNumber = data.lineNumber
    this.merchant = data.merchant
    this.amount = data.amount
    this.currencyId = data.currencyId
    this.expenseType = data.expenseType
    this.expenseTypeLabel = data.expenseTypeLabel
    this.attachment = data.attachment
    this.notes = data.notes
    this.approvedDate = data.approvedDate ? new Date(data.approvedDate) : null
    this.requester = data.requester
    this.expenseReport = data.expenseReport
    this.account = new Account(data.account)
  }

  // "RFE 29 - 1" label for the Expense # column and the modal item row
  get label(): string {
    return `RFE ${this.expenseReport.id} - ${this.lineNumber}`
  }

  get requesterFullName(): string {
    return `${this.requester.firstName} ${this.requester.lastName}`
  }

  // Location column: account.secondaryCategory.primaryCategory
  get primaryCategoryName(): Nullable<string> {
    return this.account.secondaryCategory?.primaryCategory?.name ?? null
  }

  // Department column: account.secondaryCategory
  get secondaryCategoryName(): Nullable<string> {
    return this.account.secondaryCategory?.name ?? null
  }

  // Submitted Date column / modal CREATED value
  get submittedDate(): Date {
    return new Date(this.expenseReport.createdDate)
  }
}
